import React from 'react';

import { useFormContext } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { useGamePortalPagesState } from 'state/hooks/game-portal-pages.hook';
import { z } from 'zod';

import Button from 'components/Button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from 'components/ui/dropdown-menu';

import { EBlockType, EContentType, IBlock } from '../constants';
import { camelCaseToTitleCase, mapBlockTypeToIcon } from '../utils';
import { pageFormSchema } from '../validation-schema';

const mapBlockTypeToDefaultBlock: Record<EBlockType, IBlock> = {
  [EBlockType.IMAGE]: {
    type: EBlockType.IMAGE,
    itemsPerRow: 2,
    items: [{ type: EBlockType.IMAGE, url: '' }]
  },
  [EBlockType.VIDEO]: {
    type: EBlockType.VIDEO,
    itemsPerRow: 2,
    items: [{ type: EBlockType.VIDEO, url: '' }]
  },
  [EBlockType.CTA]: {
    type: EBlockType.CTA,
    itemsPerRow: 2,
    items: [{ type: EBlockType.CTA, text: '', redirectUrl: '' }]
  },
  [EBlockType.CONTENTS_PREVIEW]: {
    type: EBlockType.CONTENTS_PREVIEW,
    contentType: EContentType.ARTICLE,
    title: '',
    maxItems: 0,
    itemsPerRow: 2
  },
  [EBlockType.HTML]: {
    type: EBlockType.HTML,
    html: '',
    itemsPerRow: 2
  },
  [EBlockType.DAILY_BONUS]: {
    type: EBlockType.DAILY_BONUS,
    itemsPerRow: 2
  },
  [EBlockType.PROGRESS_BAR]: {
    type: EBlockType.PROGRESS_BAR,
    itemsPerRow: 2
  }
};

interface IDropdownMenuItemWithIconProps {
  icon: string;
  type: EBlockType;
  handleClick: () => void;
}

const DropdownMenuItemWithIcon: React.FC<IDropdownMenuItemWithIconProps> = ({
  icon,
  type,
  handleClick
}) => {
  return (
    <DropdownMenuItem
      onClick={handleClick}
      className="text-base-popoverForeground text-sm hover:bg-base-accent cursor-pointer"
    >
      <FontAwesomeIcon icon={icon} className="text-xs" />
      {camelCaseToTitleCase(type)}
    </DropdownMenuItem>
  );
};

export const AddBlock: React.FC = () => {
  const { watch, setValue, trigger } =
    useFormContext<z.infer<typeof pageFormSchema>>();
  const { setOpenBlockIndex, setOpenItemIndex } = useGamePortalPagesState();

  const handleAddBlock = async (type: EBlockType) => {
    const currentBlocks = (watch('blocksV2') as IBlock[]) || [];
    const newBlock: IBlock = mapBlockTypeToDefaultBlock[type];
    const updatedBlocks = [...currentBlocks, newBlock];
    const newBlockIndex = updatedBlocks.length - 1;

    setValue('blocksV2', updatedBlocks, {
      shouldValidate: true,
      shouldDirty: true
    });

    setOpenBlockIndex(newBlockIndex);

    if (
      'items' in newBlock &&
      newBlock.items?.length &&
      newBlock.items.length > 0
    ) {
      setOpenItemIndex(`${newBlockIndex}-0`);
    }
    await trigger(`blocksV2.${newBlockIndex}`);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          leftIcon={<FontAwesomeIcon icon="fa-light fa-circle-plus" />}
          className="w-[190px]"
          type="button"
        >
          Add New Block
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="bg-base-popover border-base-border rounded-md w-auto min-w-[190px]"
      >
        {Object.values(EBlockType).map((blockType) => (
          <DropdownMenuItemWithIcon
            key={blockType}
            icon={mapBlockTypeToIcon[blockType]}
            type={blockType}
            handleClick={() => handleAddBlock(blockType)}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
